import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Globe, X } from "lucide-react";
import UI_EN from "../i18n/ui-en.json";
import { LANGUAGES, LANG_CODES, localizePath, splitLangPath } from "../i18n/core";
import { useI18n } from "../i18n/I18nProvider";
import { api } from "../lib/api";

export const PROMPT_DONE_KEY = "lang_prompt_done";
const BOT_RE = /bot|crawl|spider|slurp|lighthouse|facebookexternalhit|embedly/i;
const ALIASES = { nb: "no", nn: "no" };

function detectBrowserLang() {
  const list = navigator.languages?.length ? navigator.languages : [navigator.language || ""];
  for (const tag of list) {
    const base = (tag || "").toLowerCase().split("-")[0];
    const code = ALIASES[base] || base;
    if (LANG_CODES.includes(code)) return code;
  }
  return null;
}

export default function LanguagePrompt() {
  const { lang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  const [strings, setStrings] = useState(UI_EN);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return undefined;
    if (localStorage.getItem(PROMPT_DONE_KEY) || BOT_RE.test(navigator.userAgent)) return undefined;
    const detected = detectBrowserLang();
    if (!detected || detected === lang) {
      if (detected) localStorage.setItem(PROMPT_DONE_KEY, "1");
      return undefined;
    }
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/pages/ui", { params: { lang: detected } });
        if (!cancel) setStrings({ ...UI_EN, ...(data.strings || {}) });
      } catch (_e) { /* English fallback */ }
      if (cancel) return;
      setTarget(detected);
      setTimeout(() => !cancel && setVisible(true), 1200);
    })();
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Visitor changed language some other way (e.g. header selector) → retire the prompt
  useEffect(() => {
    if (target && localStorage.getItem(PROMPT_DONE_KEY)) setTarget(null);
  }, [lang, target]);

  if (!target || location.pathname.startsWith("/admin")) return null;

  const close = () => {
    localStorage.setItem(PROMPT_DONE_KEY, "1");
    setVisible(false);
    setTimeout(() => setTarget(null), 400);
  };
  const accept = () => {
    const { basePath } = splitLangPath(location.pathname);
    close();
    navigate(localizePath(basePath, target) + location.search);
  };
  const language = LANGUAGES.find((l) => l.code === target)?.name;

  return (
    <div
      data-testid="lang-prompt"
      lang={target}
      role="dialog"
      aria-live="polite"
      className={`fixed left-4 right-4 bottom-24 sm:right-auto sm:left-6 sm:bottom-6 z-[45] sm:w-[340px] bg-[#DCE9F4] text-[#1A1A1A] border border-[#B9D3E8] p-6 transition-[opacity,transform] duration-500 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
      style={{ boxShadow: "0 18px 40px -18px rgba(31,60,90,0.35)" }}
    >
      <button onClick={close} aria-label="Close" data-testid="lang-prompt-close" className="absolute top-3 right-3 p-1 text-[#5B6B7A] hover:text-[#1A1A1A] transition-colors">
        <X className="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#3F6587] mb-3">
        <Globe className="w-3.5 h-3.5" strokeWidth={1.5} /> {language}
      </div>
      <p data-testid="lang-prompt-title" className="text-xl font-light leading-snug pr-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        {strings.lang_prompt_title}
      </p>
      <div className="flex items-center gap-5 mt-5">
        <button
          onClick={accept}
          data-testid="lang-prompt-accept"
          className="bg-[#1A1A1A] text-[#F9F6F0] px-5 py-3 text-[10px] tracking-[0.25em] uppercase hover:bg-[#3F6587] transition-colors duration-300"
        >
          {(strings.lang_prompt_switch || "").replace("{language}", language)}
        </button>
        <button
          onClick={close}
          data-testid="lang-prompt-dismiss"
          className="text-[10px] tracking-[0.25em] uppercase text-[#5B6B7A] border-b border-[#5B6B7A]/40 pb-0.5 hover:text-[#1A1A1A] hover:border-[#1A1A1A] transition-colors"
        >
          {strings.lang_prompt_dismiss}
        </button>
      </div>
    </div>
  );
}
